import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star, Send, Quote, MapPin, Calendar, User, Loader2, 
  ChevronDown, Sparkles, MessageCircle, Heart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Trip type options
const tripTypes = [
  { value: "trek", label: "Trek dans le désert" },
  { value: "4x4", label: "Circuit 4x4" },
  { value: "stage", label: "Stage ou retraite" },
  { value: "bivouac", label: "Nuit en bivouac" },
  { value: "famille", label: "Voyage en famille" },
  { value: "autre", label: "Autre" },
];

// Single experience card
const ExperienceCard = ({ experience, index }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = experience.content.length > 400;
  const displayContent = expanded || !isLong 
    ? experience.content 
    : experience.content.substring(0, 400) + "...";

  // Calculate card size based on content length
  const getCardSize = () => {
    const len = experience.content.length;
    if (len < 150) return "small";
    if (len < 400) return "medium";
    return "large";
  };

  const size = getCardSize();

  return (
    <article 
      className={`group relative bg-white rounded-2xl border border-border/50 overflow-hidden
        hover:shadow-xl hover:shadow-primary/5 transition-all duration-500
        ${size === "large" ? "md:col-span-2" : ""}`}
      data-testid={`experience-card-${index}`}
    >
      {/* Decorative gradient top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-60" />
      
      {/* Number badge */}
      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
        <span className="text-primary font-serif font-bold text-sm">#{index + 1}</span>
      </div>

      <div className="p-6 md:p-8">
        {/* Quote icon */}
        <Quote className="h-8 w-8 text-primary/20 mb-4" />
        
        {/* Content */}
        <div className="mb-6">
          <p className="text-foreground/90 leading-relaxed whitespace-pre-line">
            {displayContent}
          </p>
          {isLong && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="mt-3 text-primary text-sm font-medium hover:underline flex items-center gap-1"
            >
              {expanded ? "Voir moins" : "Lire la suite"}
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>

        {/* Author info */}
        <div className="flex items-start justify-between border-t border-border/50 pt-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-serif font-bold text-lg">
              {experience.author_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-foreground">{experience.author_name}</p>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                {experience.author_location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {experience.author_location}
                  </span>
                )}
                {experience.trip_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {experience.trip_date}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Rating stars */}
          {experience.rating && (
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`h-4 w-4 ${i < experience.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} 
                />
              ))}
            </div>
          )}
        </div>

        {/* Trip type badge */}
        {experience.trip_type && (
          <div className="mt-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {tripTypes.find(t => t.value === experience.trip_type)?.label || experience.trip_type}
            </span>
          </div>
        )}
      </div>
    </article>
  );
};

// Submit form component
const SubmitForm = ({ onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    author_name: "",
    author_email: "",
    author_location: "",
    trip_type: "",
    trip_date: "",
    content: "",
    rating: 5,
  });
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author_name.trim() || !formData.content.trim()) {
      toast.error("Veuillez remplir votre nom et votre témoignage");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/experiences`, formData);
      toast.success("Merci ! Votre témoignage a été soumis et sera publié après validation.");
      setFormData({
        author_name: "",
        author_email: "",
        author_location: "",
        trip_type: "",
        trip_date: "",
        content: "",
        rating: 5,
      });
      onSubmitSuccess?.();
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-secondary/50 to-secondary rounded-3xl p-8 md:p-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <MessageCircle className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-serif text-2xl md:text-3xl font-bold mb-2">
            Partagez votre expérience
          </h2>
          <p className="text-muted-foreground">
            Vous avez voyagé avec nous ? Racontez-nous votre aventure !
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="experience-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="author_name" className="flex items-center gap-1">
                <User className="h-4 w-4" />
                Votre nom *
              </Label>
              <Input
                id="author_name"
                placeholder="Jean Dupont"
                value={formData.author_name}
                onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                required
                data-testid="input-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="author_email">Email (optionnel)</Label>
              <Input
                id="author_email"
                type="email"
                placeholder="jean@exemple.com"
                value={formData.author_email}
                onChange={(e) => setFormData(prev => ({ ...prev, author_email: e.target.value }))}
                data-testid="input-email"
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="author_location" className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                Ville / Pays
              </Label>
              <Input
                id="author_location"
                placeholder="Paris, France"
                value={formData.author_location}
                onChange={(e) => setFormData(prev => ({ ...prev, author_location: e.target.value }))}
                data-testid="input-location"
              />
            </div>

            {/* Trip type */}
            <div className="space-y-2">
              <Label htmlFor="trip_type">Type de voyage</Label>
              <Select 
                value={formData.trip_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, trip_type: value }))}
              >
                <SelectTrigger data-testid="select-trip-type">
                  <SelectValue placeholder="Sélectionnez..." />
                </SelectTrigger>
                <SelectContent>
                  {tripTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Trip date */}
            <div className="space-y-2">
              <Label htmlFor="trip_date" className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Période du voyage
              </Label>
              <Input
                id="trip_date"
                placeholder="Février 2025"
                value={formData.trip_date}
                onChange={(e) => setFormData(prev => ({ ...prev, trip_date: e.target.value }))}
                data-testid="input-date"
              />
            </div>

            {/* Rating */}
            <div className="space-y-2">
              <Label>Votre note</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`h-7 w-7 transition-colors ${
                        star <= (hoverRating || formData.rating) 
                          ? "text-amber-400 fill-amber-400" 
                          : "text-gray-300"
                      }`} 
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">Votre témoignage *</Label>
            <Textarea
              id="content"
              placeholder="Racontez-nous votre aventure dans le désert... Les moments forts, les paysages, les rencontres, ce que vous avez ressenti..."
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              rows={6}
              required
              className="resize-none"
              data-testid="input-content"
            />
            <p className="text-xs text-muted-foreground text-right">
              {formData.content.length} caractères
            </p>
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 text-base rounded-full"
            disabled={submitting}
            data-testid="submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Envoyer mon témoignage
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            En soumettant ce formulaire, vous acceptez que votre témoignage soit publié sur notre site après validation.
          </p>
        </form>
      </div>
    </div>
  );
};

// Main page component
const ExperiencesPage = () => {
  const { t } = useTranslation();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const formRef = useRef(null);

  const fetchExperiences = async () => {
    try {
      const response = await axios.get(`${API}/experiences`);
      setExperiences(response.data.experiences || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching experiences:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <SEOHead 
        title="Retour d'Expériences | Avis voyageurs | XploreSahara"
        description="Découvrez les témoignages authentiques de nos voyageurs : treks dans le désert marocain, circuits 4x4, nuits sous les étoiles. Partagez votre propre aventure !"
        keywords="avis sahara, témoignages trek maroc, retour expérience désert, voyage sahara avis"
      />
      
      <div className="min-h-screen pt-20" data-testid="experiences-page">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Heart className="h-4 w-4" />
              {total > 0 ? `${total} témoignages` : "Témoignages"}
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Retour d'<span className="text-primary">Expériences</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
              Ils sont partis avec nous explorer le désert marocain. Découvrez leurs témoignages 
              authentiques et partagez à votre tour votre aventure saharienne.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={scrollToForm}
                className="rounded-full px-8 h-12 text-base"
                data-testid="cta-share"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Partager mon expérience
              </Button>
              <Button 
                variant="outline" 
                className="rounded-full px-8 h-12 text-base"
                asChild
              >
                <a href="/circuits">Découvrir nos circuits</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Experiences Grid */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-64 bg-muted rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : experiences.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {experiences.map((exp, index) => (
                  <ExperienceCard key={exp.id} experience={exp} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Quote className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">
                  Soyez le premier à partager votre expérience !
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Submit Form Section */}
        <section ref={formRef} className="py-12 md:py-20" id="partager">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <SubmitForm onSubmitSuccess={fetchExperiences} />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 bg-gradient-to-r from-primary to-accent text-white">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4">
              Prêt à vivre votre propre aventure ?
            </h2>
            <p className="text-white/80 mb-8">
              Rejoignez nos prochains départs et créez vos propres souvenirs inoubliables.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                variant="secondary" 
                className="rounded-full px-8 h-12 text-base"
                asChild
              >
                <a href="/departures">Voir les prochains départs</a>
              </Button>
              <Button 
                variant="outline" 
                className="rounded-full px-8 h-12 text-base bg-transparent border-white text-white hover:bg-white/10"
                asChild
              >
                <a href="/contact">Nous contacter</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ExperiencesPage;
